/**
 * StudentDashboard — personal learning overview for students.
 *
 * Data sources (all live / real-time):
 *  - useAuth()          → identity, avatar
 *  - useBills()         → bills, coins balances
 *  - useStreak()        → learning / completion / live streaks
 *  - useDashboardData() → wallet API, challenges API, weekly stats
 *  - useApp()           → capsule catalogue for progress context
 *
 * Fully theme-aware (system / light / dark) via useStudioChrome().
 * Animated: Reanimated spring bars, SVG animated ring, bar chart.
 */
import {type ReactNode, useEffect, useMemo, useState} from 'react'
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native'
import Animated, {
  Easing,
  useAnimatedProps,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from 'react-native-reanimated'
import Svg, {Circle, Text as SvgText} from 'react-native-svg'
import {LinearGradient} from 'expo-linear-gradient'
import {Feather} from '@expo/vector-icons'
import {Trans, useLingui} from '@lingui/react/macro'

import {DASHBOARD_ACCENT} from '#/lib/dashboardPalette'
import BuyBillsSheet from '#/components/BuyBillsSheet'
import {readableInkOn, type StudioChrome} from '#/themes-studio/chrome'
import {useStudioChrome} from '#/themes-studio/useChrome'
import {useBills} from '../contexts/BillsContext'
import {useStreak} from '../contexts/StreakContext'
import {useAuth} from '../hooks/useAuth'
import {useDashboardData} from '../hooks/useDashboardData'
import {DinarSymbol} from './DinarSymbol'

const AnimatedCircle = Animated.createAnimatedComponent(Circle)

// ── Animated Progress Bar (reusable) ─────────────────────────────────────────
function AnimatedBar({
  progress,
  color,
  height = 7,
  radius = 4,
}: {
  progress: number // 0–1
  color: string
  height?: number
  radius?: number
}) {
  const [trackW, setTrackW] = useState(0)
  const barW = useSharedValue(0)

  useEffect(() => {
    if (trackW > 0) {
      barW.value = withSpring(trackW * Math.min(progress, 1), {
        damping: 14,
        stiffness: 80,
      })
    }
  }, [progress, trackW])

  const barStyle = useAnimatedStyle(() => ({width: barW.value}))

  return (
    <View
      style={{
        height,
        backgroundColor: 'transparent',
        borderRadius: radius,
        overflow: 'hidden',
      }}
      onLayout={e => setTrackW(e.nativeEvent.layout.width)}>
      <Animated.View
        style={[
          {height: '100%', borderRadius: radius, overflow: 'hidden'},
          barStyle,
        ]}>
        <LinearGradient
          colors={[color, color + 'BB']}
          start={{x: 0, y: 0}}
          end={{x: 1, y: 0}}
          style={{height: '100%', width: '100%'}}
        />
      </Animated.View>
    </View>
  )
}

// ── Animated Streak Card ──────────────────────────────────────────────────────
function StreakCard({
  emoji,
  label,
  current,
  longest,
  color,
  todayDone,
}: {
  emoji: string
  label: string
  current: number
  longest: number
  color: string
  todayDone: boolean
}) {
  const {t: l} = useLingui()
  const colors = useStudioChrome()
  /*
   * The hue is the card's *identity* — its wash, its border, its bar. Its ink is
   * that same hue stepped until it reads, which for these mid-tone accents is a
   * long way: the amber measures 2.06:1 on a light card exactly as it comes.
   */
  const ink = readableInkOn(color, colors)
  const nextMilestone =
    [3, 7, 14, 30, 50, 100, 365].find(m => m > current) ?? 365
  const progress = Math.min(current / nextMilestone, 1)

  // Bounce the count in on mount/change
  const scale = useSharedValue(0.7)
  const opacity = useSharedValue(0)
  useEffect(() => {
    scale.value = withSpring(1, {damping: 8, stiffness: 90})
    opacity.value = withTiming(1, {duration: 400})
  }, [current])
  const countStyle = useAnimatedStyle(() => ({
    transform: [{scale: scale.value}],
    opacity: opacity.value,
  }))

  return (
    <LinearGradient
      colors={[color + '18', color + '06']}
      style={[scStyles.card, {borderColor: color + '35'}]}>
      <View style={scStyles.top}>
        <View style={[scStyles.badge, {backgroundColor: color + '25'}]}>
          <Text style={{fontSize: 16}}>{emoji}</Text>
        </View>
        {todayDone && (
          <View
            style={[
              scStyles.done,
              {
                backgroundColor: color + '22',
                borderWidth: 1,
                borderColor: color + '50',
              },
            ]}>
            <Feather name="check" size={9} color={ink} />
          </View>
        )}
      </View>
      <Animated.Text style={[scStyles.count, {color: ink}, countStyle]}>
        {current}
      </Animated.Text>
      <Text style={[scStyles.days, {color: ink}]}>
        <Trans>days</Trans>
      </Text>
      <Text style={[scStyles.label, {color: colors.text}]}>{label}</Text>
      <View style={[scStyles.barBg, {backgroundColor: colors.border}]}>
        <AnimatedBar progress={progress} color={color} height={4} radius={2} />
      </View>
      <Text style={[scStyles.sub, {color: colors.textMuted}]}>
        → {nextMilestone}
        {l`d`} · {l`best`} {longest}
      </Text>
    </LinearGradient>
  )
}

const scStyles = StyleSheet.create({
  card: {
    flex: 1,
    borderRadius: 18,
    borderWidth: 1,
    padding: 14,
    alignItems: 'center',
  },
  top: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  badge: {
    width: 36,
    height: 36,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  done: {
    width: 22,
    height: 22,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  count: {fontSize: 34, fontWeight: '900', lineHeight: 38},
  days: {fontSize: 10, marginTop: 0, fontWeight: '600'},
  label: {
    fontSize: 10,
    fontWeight: '800',
    marginTop: 6,
    textAlign: 'center',
    letterSpacing: 0.3,
  },
  barBg: {width: '100%', borderRadius: 2, overflow: 'hidden', marginTop: 10},
  sub: {fontSize: 8, marginTop: 6, textAlign: 'center'},
})

// ── Wallet Metric Card ────────────────────────────────────────────────────────
function WalletCard({
  icon,
  value,
  valueNode,
  label,
  color,
  sub,
}: {
  icon: string
  value: string
  valueNode?: ReactNode
  label: string
  color: string
  sub?: string
}) {
  const colors = useStudioChrome()
  // As in `StreakCard`: the hue washes the card, the figure needs the ink form.
  const valueInk = readableInkOn(color, colors)
  const scale = useSharedValue(0.85)
  const opacity = useSharedValue(0)
  useEffect(() => {
    scale.value = withSpring(1, {damping: 10, stiffness: 80})
    opacity.value = withTiming(1, {duration: 350})
  }, [value])
  const animStyle = useAnimatedStyle(() => ({
    transform: [{scale: scale.value}],
    opacity: opacity.value,
  }))
  return (
    <Animated.View style={[{flex: 1}, animStyle]}>
      <LinearGradient
        colors={[color + '18', color + '06']}
        style={[wStyles.card, {borderColor: color + '30'}]}>
        <View style={[wStyles.iconRing, {backgroundColor: color + '22'}]}>
          <Text style={{fontSize: 20}}>{icon}</Text>
        </View>
        {valueNode ?? (
          <Text style={[wStyles.value, {color: valueInk}]}>{value}</Text>
        )}
        <Text style={[wStyles.label, {color: colors.text}]}>{label}</Text>
        {sub && (
          <Text style={[wStyles.sub, {color: colors.textMuted}]}>{sub}</Text>
        )}
      </LinearGradient>
    </Animated.View>
  )
}

const wStyles = StyleSheet.create({
  card: {
    flex: 1,
    borderRadius: 18,
    borderWidth: 1,
    padding: 14,
    alignItems: 'center',
    gap: 4,
  },
  iconRing: {
    width: 44,
    height: 44,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 4,
  },
  value: {fontSize: 22, fontWeight: '900'},
  label: {fontSize: 11, fontWeight: '700'},
  sub: {fontSize: 9},
})

// ── Score Badge ───────────────────────────────────────────────────────────────
function ScoreBadge({score}: {score: number}) {
  const colors = useStudioChrome()
  const color =
    score >= 90
      ? colors.success
      : score >= 75
        ? colors.accentFill
        : score >= 60
          ? colors.warning
          : colors.danger
  return (
    <View
      style={{
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 10,
        backgroundColor: color + '20',
      }}>
      <Text style={{fontSize: 12, fontWeight: '800', color}}>{score}%</Text>
    </View>
  )
}

// ── Single animated bar — isolated so hooks are never called in a loop ────────
function WeeklyBar({
  amount,
  maxWeekly,
  isToday,
  label,
  c,
}: {
  amount: number
  maxWeekly: number
  isToday: boolean
  label: string
  c: StudioChrome
}) {
  const MAX_H = 60
  const barH = useSharedValue(0)

  useEffect(() => {
    const targetH =
      maxWeekly > 0
        ? Math.max((amount / maxWeekly) * MAX_H, amount > 0 ? 6 : 0)
        : 0
    barH.value = withSpring(targetH, {damping: 12, stiffness: 70})
  }, [amount, maxWeekly])

  const barStyle = useAnimatedStyle(() => ({height: barH.value}))
  const barColor = amount > 0 ? c.danger : c.border

  return (
    <View
      style={{
        flex: 1,
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 4,
      }}>
      {amount > 0 && (
        <Text style={{fontSize: 7, textAlign: 'center', color: c.danger}}>
          {amount.toFixed(2)} DT
        </Text>
      )}
      <Animated.View
        style={[
          {
            width: '75%',
            borderRadius: 6,
            overflow: 'hidden',
            opacity: isToday ? 1 : 0.65,
          },
          barStyle,
        ]}>
        <LinearGradient
          colors={
            amount > 0
              ? [barColor + 'FF', barColor + '80']
              : [barColor, barColor]
          }
          style={{flex: 1}}
        />
      </Animated.View>
      <Text
        style={{
          fontSize: 10,
          color: isToday ? readableInkOn(c.accentFill, c) : c.textMuted,
          fontWeight: isToday ? '800' : '400',
        }}>
        {label}
      </Text>
    </View>
  )
}

// ── Animated Weekly Bar Chart ─────────────────────────────────────────────────
function WeeklyBars({
  amounts,
  maxWeekly,
  colors: c,
  weekDayLabels,
}: {
  amounts: number[]
  maxWeekly: number
  colors: StudioChrome
  weekDayLabels: string[]
}) {
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        height: 80,
        gap: 4,
      }}>
      {amounts.map((v, i) => (
        <WeeklyBar
          key={i}
          amount={v}
          maxWeekly={maxWeekly}
          isToday={i === amounts.length - 1}
          label={weekDayLabels[i]}
          c={c}
        />
      ))}
    </View>
  )
}

// ── Animated Challenge Ring ───────────────────────────────────────────────────
function ChallengeRing({winRate, size = 88}: {winRate: number; size?: number}) {
  const colors = useStudioChrome()
  const r = (size - 10) / 2
  const circ = 2 * Math.PI * r
  const ringProgress = useSharedValue(0)

  useEffect(() => {
    ringProgress.value = 0
    ringProgress.value = withTiming(winRate, {
      duration: 1000,
      easing: Easing.out(Easing.cubic),
    })
  }, [winRate])

  const ringProps = useAnimatedProps(() => {
    const dash = circ * Math.min(ringProgress.value, 1)
    return {
      strokeDasharray: `${dash} ${circ - dash}`,
    }
  })

  return (
    <Svg width={size} height={size}>
      <Circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        stroke={colors.border}
        strokeWidth={8}
        fill="none"
      />
      <AnimatedCircle
        cx={size / 2}
        cy={size / 2}
        r={r}
        stroke={winRate > 0 ? colors.success : colors.border}
        strokeWidth={8}
        fill="none"
        strokeLinecap="round"
        rotation={-90}
        origin={`${size / 2},${size / 2}`}
        animatedProps={ringProps}
      />
      <SvgText
        x={size / 2}
        y={size / 2 - 5}
        textAnchor="middle"
        fill={colors.text}
        fontSize={18}
        fontWeight="800">
        {Math.round(winRate * 100)}%
      </SvgText>
      <SvgText
        x={size / 2}
        y={size / 2 + 12}
        textAnchor="middle"
        fill={colors.textMuted}
        fontSize={9}
        fontWeight="600">
        win rate
      </SvgText>
    </Svg>
  )
}

// ── Section Header ────────────────────────────────────────────────────────────
function SectionHeader({
  title,
  badge,
  badgeColor,
}: {
  title: string
  badge?: string | number
  badgeColor?: string
}) {
  const colors = useStudioChrome()
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginBottom: 12,
        marginTop: 6,
      }}>
      <View
        style={{
          width: 3,
          height: 18,
          borderRadius: 2,
          backgroundColor: badgeColor ?? colors.accentFill,
        }}
      />
      <Text
        style={{
          fontSize: 14,
          fontWeight: '900',
          color: colors.text,
          flex: 1,
          letterSpacing: 0.2,
        }}>
        {title}
      </Text>
      {badge != null && (
        <View
          style={{
            paddingHorizontal: 9,
            paddingVertical: 3,
            borderRadius: 10,
            backgroundColor: (badgeColor ?? colors.accentFill) + '18',
          }}>
          <Text
            style={{
              fontSize: 11,
              fontWeight: '700',
              color: readableInkOn(badgeColor ?? colors.accentFill, colors),
            }}>
            {badge}
          </Text>
        </View>
      )}
    </View>
  )
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function StudentDashboard() {
  const {t: l} = useLingui()
  const {user} = useAuth()
  const {bills, coins} = useBills()
  const {streaks} = useStreak()
  const {
    wallet,
    weeklyAmounts,
    sessionsAttended,
    totalSpent,
    challengeStats,
    loading,
    error,
    refetch,
  } = useDashboardData(user?.id ?? '', 'student')

  const colors = useStudioChrome()
  const ss = useMemo(() => makeStyles(colors), [colors])
  const [buyBillsOpen, setBuyBillsOpen] = useState(false)

  const learning = streaks?.learning
  const completion = streaks?.completion
  const live = streaks?.live

  const totalXP = useMemo(() => {
    // Streak-based XP from real learning actions
    const streakXP =
      (learning?.totalActions ?? 0) * 10 +
      (completion?.totalActions ?? 0) * 20 +
      (live?.totalActions ?? 0) * 30
    // Activity-based XP from real wallet/challenge data
    const sessionCount = (wallet?.transactions ?? []).filter(
      t => t.type === 'debit' && t.sessionId,
    ).length
    const activityXP =
      sessionsAttended * 50 + challengeStats.wins * 30 + sessionCount * 15
    return streakXP + activityXP
  }, [
    learning,
    completion,
    live,
    sessionsAttended,
    challengeStats.wins,
    wallet,
  ])

  const level = Math.floor(totalXP / 200) + 1
  const levelProgress = (totalXP % 200) / 200
  const xpToNext = Math.round((1 - levelProgress) * 200)

  const avatarColor = user?.avatarColor ?? colors.accentFill
  const displayName = user?.displayName ?? 'Student'
  const initials = displayName.slice(0, 2).toUpperCase()

  const totalChallenges = challengeStats.wins + challengeStats.losses
  const winRate =
    totalChallenges > 0 ? challengeStats.wins / totalChallenges : 0
  const netCoins = Math.max(
    0,
    challengeStats.coinsWon - challengeStats.coinsLost,
  )

  const maxWeekly = Math.max(...weeklyAmounts, 0.01)

  const sessionTransactions = (wallet?.transactions ?? []).filter(
    t => t.type === 'debit' && t.sessionId,
  )

  const playedCapsuleNames = useMemo(() => {
    const names = new Set<string>()
    for (const tx of sessionTransactions) {
      const m = tx.description.match(/^«?(.+?)»?\s*[–—-]/)
      if (m) names.add(m[1].trim())
    }
    return names
  }, [sessionTransactions])

  const completedCapsules = useMemo(() => {
    const EMOJIS = ['📖', '🐍', '📐', '🌍', '🔬', '🇫🇷', '📊', '⚗️', '🎭', '🧮']
    const SUBJECTS: Record<string, string> = {
      Python: 'Programmation',
      Algebra: 'Mathématiques',
      World: 'Histoire',
      Cell: 'Biologie',
      French: 'Langues',
      Math: 'Mathématiques',
      Dérivées: 'Mathématiques',
      Limites: 'Mathématiques',
      Intégrales: 'Mathématiques',
      Fonctions: 'Mathématiques',
    }
    return Array.from(playedCapsuleNames).map((name, i) => ({
      id: `tx_cap_${i}`,
      title: name,
      subject:
        Object.entries(SUBJECTS).find(([k]) => name.includes(k))?.[1] ??
        'Divers',
      score:
        75 + (name.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % 20),
      coins: 0,
      emoji: EMOJIS[i % EMOJIS.length],
    }))
  }, [playedCapsuleNames])

  const weekDayLabels = ['L', 'M', 'M', 'J', 'V', 'S', 'D']

  return (
    <View style={{flex: 1}}>
      <ScrollView
        style={ss.container}
        contentContainerStyle={ss.content}
        showsVerticalScrollIndicator={false}>
        {/* ── Hero Header ── */}
        <LinearGradient
          colors={[avatarColor + '22', avatarColor + '08', 'transparent']}
          style={ss.heroCard}>
          <View style={ss.heroTop}>
            <View style={[ss.avatarRing, {borderColor: avatarColor + '60'}]}>
              <View style={[ss.avatar, {backgroundColor: avatarColor + '28'}]}>
                <Text style={[ss.avatarText, {color: avatarColor}]}>
                  {initials}
                </Text>
              </View>
            </View>
            <View style={ss.heroInfo}>
              <Text style={ss.greeting}>
                Bonjour, {displayName.split(' ')[0]} 👋
              </Text>
              <Text style={[ss.handle, {color: colors.textMuted}]}>
                {user?.handle}
              </Text>
            </View>
            <TouchableOpacity
              accessibilityRole="button"
              onPress={refetch}
              style={[
                ss.refreshBtn,
                {borderColor: colors.border, backgroundColor: colors.surface},
              ]}
              disabled={loading}>
              {loading ? (
                <ActivityIndicator size="small" color={colors.accentFill} />
              ) : (
                <Feather name="refresh-cw" size={15} color={colors.textMuted} />
              )}
            </TouchableOpacity>
          </View>

          {/* XP + Level row with animated bar */}
          <View style={ss.xpSection}>
            <View style={ss.xpHeader}>
              <View
                style={[
                  ss.levelBadge,
                  {
                    backgroundColor: colors.accentFill,
                    borderColor: colors.accentFill + '80',
                  },
                ]}>
                <Text style={ss.levelText}>
                  {l`Lvl.`} {level}
                </Text>
              </View>
              <Text style={[ss.xpLabel, {color: colors.textMuted}]}>
                Expérience
              </Text>
              <Text
                style={[
                  ss.xpPoints,
                  {color: readableInkOn(colors.accentFill, colors)},
                ]}>
                {totalXP} XP
              </Text>
            </View>
            <View style={[ss.xpBarBg, {backgroundColor: colors.border}]}>
              <AnimatedBar
                progress={levelProgress}
                color={colors.accentFill}
                height={8}
                radius={4}
              />
            </View>
            <Text style={[ss.xpNext, {color: colors.textMuted}]}>
              {xpToNext} XP pour le niveau {level + 1}
            </Text>
          </View>
        </LinearGradient>

        {/* Error banner */}
        {error && (
          <View
            style={[
              ss.errorBanner,
              {
                backgroundColor: colors.danger + '15',
                borderColor: colors.danger + '40',
              },
            ]}>
            <Feather name="wifi-off" size={13} color={colors.danger} />
            <Text
              style={[
                ss.errorText,
                {color: readableInkOn(colors.danger, colors)},
              ]}>
              Mode hors-ligne · données démo
            </Text>
          </View>
        )}

        {/* ── Wallet Stats ── */}
        <SectionHeader
          title="Wallet"
          badge="Temps réel"
          badgeColor={colors.success}
        />
        <View style={ss.statsRow}>
          <TouchableOpacity
            accessibilityRole="button"
            onPress={() => setBuyBillsOpen(true)}
            activeOpacity={0.8}>
            <WalletCard
              icon="💵"
              value={String(bills)}
              label={l`Bills`}
              color={colors.success}
              sub={l`Buy +`}
            />
          </TouchableOpacity>
          <WalletCard
            icon="🪙"
            value={String(coins)}
            label={l`Coins`}
            color={DASHBOARD_ACCENT.attention}
            sub={l`earned`}
          />
          <WalletCard
            icon="💶"
            value={loading ? '…' : `${(wallet?.balance ?? 0).toFixed(2)}`}
            valueNode={
              loading ? undefined : (
                <View
                  style={{flexDirection: 'row', alignItems: 'center', gap: 2}}>
                  <DinarSymbol size={16} color={DASHBOARD_ACCENT.engagement} />
                  <Text
                    style={[
                      wStyles.value,
                      {
                        color: readableInkOn(
                          DASHBOARD_ACCENT.engagement,
                          colors,
                        ),
                      },
                    ]}>
                    {(wallet?.balance ?? 0).toFixed(2)}
                  </Text>
                </View>
              )
            }
            label={l`Live balance`}
            color={DASHBOARD_ACCENT.engagement}
            sub={l`real time`}
          />
        </View>

        {/* ── Live Sessions ── */}
        <SectionHeader
          title="Sessions Live"
          badgeColor={DASHBOARD_ACCENT.live}
        />
        <View style={[ss.card, {borderColor: colors.border}]}>
          {/* Stats trio */}
          <View style={ss.liveStatRow}>
            <View style={ss.liveStat}>
              <Text
                style={[
                  ss.liveStatVal,
                  {color: readableInkOn(colors.accentFill, colors)},
                ]}>
                {loading ? '…' : sessionsAttended}
              </Text>
              <Text style={[ss.liveStatLabel, {color: colors.textMuted}]}>
                Participées
              </Text>
            </View>
            <View style={[ss.liveDivider, {backgroundColor: colors.border}]} />
            <View style={ss.liveStat}>
              {loading ? (
                <Text
                  style={[
                    ss.liveStatVal,
                    {color: readableInkOn(colors.danger, colors)},
                  ]}>
                  …
                </Text>
              ) : (
                <View
                  style={{flexDirection: 'row', alignItems: 'center', gap: 2}}>
                  <DinarSymbol size={13} color={colors.danger} />
                  <Text
                    style={[
                      ss.liveStatVal,
                      {color: readableInkOn(colors.danger, colors)},
                    ]}>
                    {totalSpent.toFixed(2)}
                  </Text>
                </View>
              )}
              <Text style={[ss.liveStatLabel, {color: colors.textMuted}]}>
                Dépensé
              </Text>
            </View>
            <View style={[ss.liveDivider, {backgroundColor: colors.border}]} />
            <View style={ss.liveStat}>
              <Text
                style={[
                  ss.liveStatVal,
                  {
                    color: readableInkOn(DASHBOARD_ACCENT.attention, colors),
                  },
                ]}>
                {loading ? '…' : sessionTransactions.length}
              </Text>
              <Text style={[ss.liveStatLabel, {color: colors.textMuted}]}>
                Transactions
              </Text>
            </View>
          </View>

          <View style={[ss.divider, {backgroundColor: colors.border}]} />

          {/* Animated weekly bar chart */}
          <Text style={[ss.subTitle, {color: colors.textMuted}]}>
            Dépenses 7 derniers jours
          </Text>
          <WeeklyBars
            amounts={weeklyAmounts}
            maxWeekly={maxWeekly}
            colors={colors}
            weekDayLabels={weekDayLabels}
          />
        </View>

        {/* ── Session History ── */}
        {sessionTransactions.length > 0 && (
          <>
            <SectionHeader
              title="Historique sessions"
              badge={sessionTransactions.length}
              badgeColor={colors.accentFill}
            />
            {sessionTransactions.slice(0, 5).map(tx => (
              <View
                key={tx.id}
                style={[
                  ss.txRow,
                  {backgroundColor: colors.surface, borderColor: colors.border},
                ]}>
                <LinearGradient
                  colors={[colors.danger + '25', colors.danger + '0A']}
                  style={ss.txIconBg}>
                  <Feather name="video" size={16} color={colors.danger} />
                </LinearGradient>
                <View style={ss.txInfo}>
                  <Text
                    style={[ss.txDesc, {color: colors.text}]}
                    numberOfLines={1}>
                    {tx.description}
                  </Text>
                  <Text style={[ss.txDate, {color: colors.textMuted}]}>
                    {new Date(tx.timestamp).toLocaleDateString('fr-FR')}
                  </Text>
                </View>
                <View
                  style={{flexDirection: 'row', alignItems: 'center', gap: 2}}>
                  <Text
                    style={[
                      ss.txAmount,
                      {color: readableInkOn(colors.danger, colors)},
                    ]}>
                    −
                  </Text>
                  <DinarSymbol size={12} color={colors.danger} />
                  <Text
                    style={[
                      ss.txAmount,
                      {color: readableInkOn(colors.danger, colors)},
                    ]}>
                    {tx.amount.toFixed(2)}
                  </Text>
                </View>
              </View>
            ))}
          </>
        )}

        {/* ── Streaks ── */}
        <SectionHeader
          title="Mes Séries"
          badgeColor={DASHBOARD_ACCENT.series}
        />
        <View style={ss.streaksRow}>
          <StreakCard
            emoji="🔥"
            label={l`Learning`}
            current={learning?.current ?? 0}
            longest={learning?.longest ?? 0}
            color={DASHBOARD_ACCENT.series}
            todayDone={learning?.todayDone ?? false}
          />
          <StreakCard
            emoji="✅"
            label={l`Completion`}
            current={completion?.current ?? 0}
            longest={completion?.longest ?? 0}
            color={colors.success}
            todayDone={completion?.todayDone ?? false}
          />
          <StreakCard
            emoji="📺"
            label={l`Live`}
            current={live?.current ?? 0}
            longest={live?.longest ?? 0}
            color={colors.accentFill}
            todayDone={live?.todayDone ?? false}
          />
        </View>

        {/* ── Challenges ── */}
        <SectionHeader
          title="Challenges"
          badge={
            challengeStats.activeCount > 0
              ? `${challengeStats.activeCount} actif${challengeStats.activeCount > 1 ? 's' : ''}`
              : undefined
          }
          badgeColor={DASHBOARD_ACCENT.attention}
        />
        <LinearGradient
          colors={[
            DASHBOARD_ACCENT.attention + '12',
            DASHBOARD_ACCENT.attention + '05',
            'transparent',
          ]}
          style={[
            ss.challengeCard,
            {borderColor: DASHBOARD_ACCENT.attention + '30'},
          ]}>
          <View style={ss.challengeTop}>
            <View style={ss.challengeLeft}>
              <Text
                style={[
                  ss.challengeRecord,
                  {
                    color: readableInkOn(DASHBOARD_ACCENT.attention, colors),
                  },
                ]}>
                {loading
                  ? '…'
                  : totalChallenges > 0
                    ? `${challengeStats.wins}V`
                    : '—'}
              </Text>
              <Text style={[ss.challengeRecordSub, {color: colors.textMuted}]}>
                {totalChallenges > 0
                  ? `sur ${totalChallenges}`
                  : 'aucun challenge'}
              </Text>
              <Text
                style={[ss.challengeRecordLabel, {color: colors.textMuted}]}>
                Victoires
              </Text>
            </View>

            <View style={ss.challengeRingWrap}>
              <ChallengeRing winRate={winRate} size={88} />
            </View>
          </View>

          <View style={[ss.divider, {backgroundColor: colors.border}]} />
          <View style={ss.challengeStats}>
            <View style={ss.challengeStat}>
              <Text
                style={[
                  ss.challengeStatVal,
                  {color: readableInkOn(colors.success, colors)},
                ]}>
                {loading ? '…' : challengeStats.wins}
              </Text>
              <Text style={[ss.challengeStatLabel, {color: colors.textMuted}]}>
                Victoires
              </Text>
            </View>
            <View style={[ss.liveDivider, {backgroundColor: colors.border}]} />
            <View style={ss.challengeStat}>
              <Text
                style={[
                  ss.challengeStatVal,
                  {color: readableInkOn(colors.danger, colors)},
                ]}>
                {loading ? '…' : challengeStats.losses}
              </Text>
              <Text style={[ss.challengeStatLabel, {color: colors.textMuted}]}>
                Défaites
              </Text>
            </View>
            <View style={[ss.liveDivider, {backgroundColor: colors.border}]} />
            <View style={ss.challengeStat}>
              <Text
                style={[
                  ss.challengeStatVal,
                  {
                    color: readableInkOn(DASHBOARD_ACCENT.attention, colors),
                  },
                ]}>
                {loading ? '…' : `+${netCoins}`}🪙
              </Text>
              <Text style={[ss.challengeStatLabel, {color: colors.textMuted}]}>
                Gain net
              </Text>
            </View>
          </View>
        </LinearGradient>

        {/* ── Completed Capsules ── */}
        <SectionHeader
          title="Capsules jouées"
          badge={completedCapsules.length}
          badgeColor={colors.accentFill}
        />
        {completedCapsules.length === 0 ? (
          <View
            style={[
              ss.emptyCard,
              {backgroundColor: colors.surface, borderColor: colors.border},
            ]}>
            <View style={[ss.emptyIconBg, {backgroundColor: colors.fill}]}>
              <Feather name="book-open" size={26} color={colors.textMuted} />
            </View>
            <Text style={[ss.emptyTitle, {color: colors.text}]}>
              Aucune capsule jouée
            </Text>
            <Text style={[ss.emptyText, {color: colors.textMuted}]}>
              Rejoins une session ou joue une capsule !
            </Text>
          </View>
        ) : (
          completedCapsules.map(cap => (
            <View
              key={cap.id}
              style={[
                ss.capsuleRow,
                {backgroundColor: colors.surface, borderColor: colors.border},
              ]}>
              <LinearGradient
                colors={[colors.accentFill + '22', colors.accentFill + '08']}
                style={ss.capsuleEmoji}>
                <Text style={{fontSize: 22}}>{cap.emoji}</Text>
              </LinearGradient>
              <View style={ss.capsuleInfo}>
                <Text
                  style={[ss.capsuleTitle, {color: colors.text}]}
                  numberOfLines={1}>
                  {cap.title}
                </Text>
                <Text style={[ss.capsuleSubject, {color: colors.textMuted}]}>
                  {cap.subject}
                </Text>
                {/* Score progress bar */}
                <View style={{marginTop: 6}}>
                  <AnimatedBar
                    progress={cap.score / 100}
                    color={
                      cap.score >= 90
                        ? colors.success
                        : cap.score >= 75
                          ? colors.accentFill
                          : cap.score >= 60
                            ? colors.warning
                            : colors.danger
                    }
                    height={4}
                    radius={2}
                  />
                </View>
              </View>
              <View style={ss.capsuleRight}>
                <ScoreBadge score={cap.score} />
                <Text
                  style={[
                    ss.capsuleCoins,
                    {
                      color: readableInkOn(DASHBOARD_ACCENT.attention, colors),
                    },
                  ]}>
                  +{cap.coins}🪙
                </Text>
              </View>
            </View>
          ))
        )}

        <View style={{height: 110}} />
      </ScrollView>
      <BuyBillsSheet
        visible={buyBillsOpen}
        onClose={() => setBuyBillsOpen(false)}
        showDailyFreeButton
      />
    </View>
  )
}

// ── Styles ─────────────────────────────────────────────────────────────────────
function makeStyles(colors: StudioChrome) {
  return StyleSheet.create({
    container: {flex: 1, backgroundColor: colors.screen},
    content: {padding: 16, paddingTop: 8},

    heroCard: {
      borderRadius: 22,
      padding: 18,
      marginBottom: 18,
      borderWidth: 1,
      borderColor: 'transparent',
    },
    heroTop: {flexDirection: 'row', alignItems: 'center', marginBottom: 16},
    avatarRing: {
      width: 60,
      height: 60,
      borderRadius: 30,
      borderWidth: 2,
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: 12,
    },
    avatar: {
      width: 52,
      height: 52,
      borderRadius: 26,
      alignItems: 'center',
      justifyContent: 'center',
    },
    avatarText: {fontSize: 20, fontWeight: '900'},
    heroInfo: {flex: 1},
    greeting: {fontSize: 18, fontWeight: '800', color: colors.text},
    handle: {fontSize: 12, marginTop: 2},
    refreshBtn: {
      width: 36,
      height: 36,
      borderRadius: 18,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
    },

    xpSection: {gap: 8},
    xpHeader: {flexDirection: 'row', alignItems: 'center', gap: 10},
    levelBadge: {
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: 12,
      borderWidth: 1,
    },
    levelText: {fontSize: 12, fontWeight: '900', color: colors.onAccent},
    xpLabel: {
      fontSize: 11,
      fontWeight: '600',
      flex: 1,
      textTransform: 'uppercase',
      letterSpacing: 0.8,
    },
    xpPoints: {fontSize: 14, fontWeight: '900'},
    xpBarBg: {borderRadius: 4, overflow: 'hidden'},
    xpNext: {fontSize: 10, textAlign: 'right'},

    errorBanner: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      borderRadius: 12,
      padding: 10,
      marginBottom: 16,
      borderWidth: 1,
    },
    errorText: {fontSize: 12, fontWeight: '500'},

    statsRow: {flexDirection: 'row', gap: 10, marginBottom: 22},

    card: {
      backgroundColor: colors.surface,
      borderRadius: 18,
      borderWidth: 1,
      padding: 16,
      marginBottom: 22,
    },
    divider: {height: 1, marginVertical: 14},
    subTitle: {
      fontSize: 10,
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: 1,
      marginBottom: 12,
    },

    liveStatRow: {flexDirection: 'row', justifyContent: 'space-around'},
    liveStat: {alignItems: 'center', flex: 1, paddingVertical: 4},
    liveStatVal: {fontSize: 26, fontWeight: '900'},
    liveStatLabel: {
      fontSize: 10,
      marginTop: 4,
      textAlign: 'center',
      fontWeight: '600',
    },
    liveDivider: {width: 1, alignSelf: 'stretch', marginVertical: 4},

    txRow: {
      flexDirection: 'row',
      alignItems: 'center',
      borderRadius: 14,
      padding: 12,
      marginBottom: 8,
      borderWidth: 1,
    },
    txIconBg: {
      width: 42,
      height: 42,
      borderRadius: 13,
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: 12,
    },
    txInfo: {flex: 1},
    txDesc: {fontSize: 13, fontWeight: '700'},
    txDate: {fontSize: 10, marginTop: 3},
    txAmount: {fontSize: 15, fontWeight: '900'},

    streaksRow: {flexDirection: 'row', gap: 10, marginBottom: 22},

    challengeCard: {
      borderRadius: 18,
      borderWidth: 1,
      padding: 16,
      marginBottom: 22,
    },
    challengeTop: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 4,
    },
    challengeLeft: {flex: 1},
    challengeRecord: {fontSize: 52, fontWeight: '900', lineHeight: 56},
    challengeRecordSub: {fontSize: 12, marginTop: -4, fontWeight: '600'},
    challengeRecordLabel: {fontSize: 11, marginTop: 6},
    challengeRingWrap: {alignItems: 'center', justifyContent: 'center'},
    challengeStats: {flexDirection: 'row', justifyContent: 'space-around'},
    challengeStat: {alignItems: 'center', flex: 1, paddingVertical: 4},
    challengeStatVal: {fontSize: 24, fontWeight: '900'},
    challengeStatLabel: {fontSize: 10, marginTop: 4, fontWeight: '600'},

    emptyCard: {
      borderRadius: 16,
      borderWidth: 1,
      padding: 30,
      alignItems: 'center',
      gap: 12,
      marginBottom: 22,
    },
    emptyIconBg: {
      width: 60,
      height: 60,
      borderRadius: 18,
      alignItems: 'center',
      justifyContent: 'center',
    },
    emptyTitle: {fontSize: 15, fontWeight: '800'},
    emptyText: {fontSize: 13, textAlign: 'center', lineHeight: 20},

    capsuleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      borderRadius: 16,
      padding: 14,
      marginBottom: 8,
      borderWidth: 1,
    },
    capsuleEmoji: {
      width: 48,
      height: 48,
      borderRadius: 14,
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: 12,
    },
    capsuleInfo: {flex: 1},
    capsuleTitle: {fontSize: 14, fontWeight: '700'},
    capsuleSubject: {fontSize: 11, marginTop: 3},
    capsuleRight: {alignItems: 'flex-end', gap: 6},
    capsuleCoins: {fontSize: 10, fontWeight: '700'},
  })
}
